// ✅ src/pages/VehicleV2/DayWiseLubeOutwardReport.jsx  (FULL UPDATED FILE)
// - Calls backend:
//    app.use("/api/daywiseLubeOutwardReport", daywiseLubeOutwardReport);
// - APIs used:
//    GET  /api/daywiseLubeOutwardReport/init
//    GET  /api/daywiseLubeOutwardReport/highlights
//    GET  /api/daywiseLubeOutwardReport/list
//
// ✅ Print routing added:
//    navigate("/vehicle/DayWiseLubeOutwardReport_Print", { state:{...all filters...} })

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaFileExport,
  FaEye,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const buildFinYearHeaders = () => {
  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("x-finyearid") ||
    localStorage.getItem("FinYearId") ||
    "";

  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("x-finyear") ||
    localStorage.getItem("FinYear") ||
    "";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("x-branchtype") ||
    "";

  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("x-branchid") ||
    "";

  const h = {};
  if (finYearId) h["x-finyearid"] = finYearId;
  if (finYear) h["x-finyear"] = finYear;
  if (branchType) h["x-branchtype"] = branchType;
  if (branchId) h["x-branchid"] = branchId;
  return h;
};

const withSelect = (arr) => [{ label: "Select", value: "" }, ...(arr || [])];

const DayWiseLubeOutwardReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // NOTE: tailwind dynamic class strings may need safelist in tailwind.config
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [showFilters, setShowFilters] = useState(true);

  // server data + dropdowns
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]); // {label,value,text}
  const [years, setYears] = useState([]); // [{label,value}]
  const [itemCategories, setItemCategories] = useState([]);
  const [itemNames, setItemNames] = useState([]);

  const [highlightItems, setHighlightItems] = useState(new Set()); // item-name highlight
  const [userLock, setUserLock] = useState({ branchLocked: false, branchValue: "" });

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // list result (from API)
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columns, setColumns] = useState([]);
  const [totalDays, setTotalDays] = useState(0);

  // filters
  const [filters, setFilters] = useState({
    branch: "",
    month: "", // monthValue (1..12)
    year: "",
    itemCategory: "", // fixed 37
    itemName: "", // itemId
  });

  // derived monthText for API
  const monthText = useMemo(() => {
    const m = months.find((x) => String(x.value) === String(filters.month));
    return m?.text || m?.label || "";
  }, [months, filters.month]);

  // selected item label (for print header)
  const selectedItemLabel = useMemo(() => {
    const it = itemNames.find((x) => String(x.value) === String(filters.itemName));
    return it?.label || "";
  }, [itemNames, filters.itemName]);

  /* ---------------- init ---------------- */
  useEffect(() => {
    const run = async () => {
      setLoadingInit(true);
      try {
        const headers = buildFinYearHeaders();
        const branchType = headers["x-branchtype"] || "";
        const branchId = headers["x-branchid"] || "";

        const rs = await axios.get("https://nissi-india.com/api/daywiseLubeOutwardReport/init", {
          headers,
          params: { branchType, branchId },
        });

        if (!rs.data?.success) throw new Error(rs.data?.message || "Init failed");

        setBranches(withSelect(rs.data.branches));
        setMonths(withSelect(rs.data.months));
        setYears(withSelect((rs.data.years || []).map((y) => ({ label: y, value: y }))));
        setItemCategories(withSelect(rs.data.itemCategories));
        setItemNames(withSelect(rs.data.itemNames));

        // fixed category (37)
        const fixedCat = rs.data?.fixed?.itemCategoryId || "37";
        setFilters((p) => ({ ...p, itemCategory: fixedCat }));

        // branch lock (non-HO)
        const lock = rs.data?.userLock || { branchLocked: false, branchValue: "" };
        setUserLock(lock);
        if (lock?.branchLocked && lock?.branchValue) {
          setFilters((p) => ({ ...p, branch: String(lock.branchValue) }));
        }
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Failed to load init data");
      } finally {
        setLoadingInit(false);
      }
    };
    run();
  }, []);

  /* ---------------- highlight items (SelectingItemsMadhan) ---------------- */
  useEffect(() => {
    const canFetch = !!filters.branch && !!monthText && !!filters.year && !loadingInit;

    if (!canFetch) {
      setHighlightItems(new Set());
      return;
    }

    const run = async () => {
      try {
        const headers = buildFinYearHeaders();
        const rs = await axios.get("https://nissi-india.com/api/daywiseLubeOutwardReport/highlights", {
          headers,
          params: {
            branchId: filters.branch,
            monthText: monthText,
            year: filters.year,
          },
        });

        if (!rs.data?.success) return;

        const list = rs.data?.itemNamesToHighlight || [];
        setHighlightItems(new Set(list.map((x) => String(x).trim().toLowerCase())));
      } catch {
        setHighlightItems(new Set());
      }
    };

    run();
  }, [filters.branch, monthText, filters.year, loadingInit]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      branch: userLock?.branchLocked ? String(userLock.branchValue || "") : "",
      month: "",
      year: "",
      itemName: "",
      itemCategory: p.itemCategory || "37",
    }));
    setRows([]);
    setTotalRows(0);
    setColumns([]);
    setTotalDays(0);
    setCurrentPage(1);
    setHighlightItems(new Set());
    toast.info("Filters cleared!");
  };

  const fetchList = async (page = 1, ignoreItem = false) => {
    if (!filters.month) {
      toast.warning("Please Select the Month to search");
      return;
    }
    if (!filters.year) {
      toast.warning("Please Select Year to search");
      return;
    }
    if (!filters.branch) {
      toast.warning("Please Select Branch to search");
      return;
    }

    setLoadingList(true);
    try {
      const headers = buildFinYearHeaders();

      const rs = await axios.get("https://nissi-india.com/api/daywiseLubeOutwardReport/list", {
        headers,
        params: {
          branchId: filters.branch,
          monthValue: filters.month,
          monthText: monthText,
          year: filters.year,
          itemCategoryId: filters.itemCategory || "37",
          itemId: ignoreItem ? "Select" : (filters.itemName || "Select"),
          page,
          pageSize: itemsPerPage,
        },
      });

      if (!rs.data?.success) throw new Error(rs.data?.message || "Search failed");

      setRows(rs.data.rows || []);
      setTotalRows(rs.data.total || 0);
      setColumns(rs.data.columns || []);
      setTotalDays(rs.data.totaldays || 0);
      setCurrentPage(page);

      toast.success(`Found ${rs.data.total || 0} record(s)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to fetch report");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = async () => {
    await fetchList(1, false);
  };

  const handleViewAll = async () => {
    if (!filters.month || !filters.year) {
      toast.info("Please select Month and Year to view records.");
      return;
    }
    await fetchList(1, true);
  };

  // ✅ PRINT: redirect with all filter params
  const handlePrint = () => {
    if (!filters.month) {
      toast.warning("Please Select the Month to print");
      return;
    }
    if (!filters.year) {
      toast.warning("Please Select Year to print");
      return;
    }
    if (!filters.branch) {
      toast.warning("Please Select Branch to print");
      return;
    }

    navigate("/vehicle/DayWiseLubeOutwardReport_Print", {
      state: {
        branchId: filters.branch,
        monthValue: filters.month, // 1..12
        monthText: monthText,      // e.g. "February"
        year: filters.year,
        itemCategoryId: filters.itemCategory || "37",
        itemId: filters.itemName || "Select",

        // display-only
        itemNameText: selectedItemLabel,

        // optional info (for header/debug)
        totalDays,
        totalRows,
      },
    });
  };

  const handleExport = () => {
    toast.info("Export not wired yet (add /exportCsv backend if needed).");
  };

  // pagination
  const totalPages = Math.max(1, Math.ceil(totalRows / itemsPerPage));
  const currentItems = rows;

  const paginate = (pageNumber) => {
    const p = Math.max(1, Math.min(totalPages, pageNumber));
    fetchList(p, false);
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const optionStyle = (label) => {
    const key = String(label || "").trim().toLowerCase();
    if (!key) return undefined;
    if (highlightItems.has(key)) return { backgroundColor: "#00e6e6" };
    return undefined;
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAY WISE LUBE OUTWARD REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Day Wise Lube Outward Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRows} Records
              </span>

              {loadingList && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              {/* ✅ PRINT BUTTON */}
              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded-lg transition-colors`}
                title="Print"
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                title="Export"
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                title="Back"
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDark ? "text-white" : "text-gray-900"}`}>
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!!userLock?.branchLocked}
                    title={userLock?.branchLocked ? "Branch locked for non-HO users" : ""}
                  >
                    {branches.map((b) => (
                      <option key={b.value || b.label} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    {months.map((m) => (
                      <option key={m.value || m.label} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    {years.map((y) => (
                      <option key={y.value || y.label} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Item Category</label>
                  <select name="itemCategory" value={filters.itemCategory} onChange={handleChange} className={inputClass} disabled>
                    {itemCategories.map((c) => (
                      <option key={c.value || c.label} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    (Fixed to Category ID: {filters.itemCategory || "37"})
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Item Name</label>
                  <select name="itemName" value={filters.itemName} onChange={handleChange} className={inputClass}>
                    {itemNames.map((it) => (
                      <option key={it.value || it.label} value={it.value} style={optionStyle(it.label)}>
                        {it.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {totalDays ? (
                      <span>
                        Total Days in selected month: <b>{totalDays}</b>
                      </span>
                    ) : (
                      <span>&nbsp;</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                  disabled={loadingInit || loadingList}
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    disabled={loadingInit || loadingList}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loadingInit || loadingList}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                  >
                    <FaSearch />
                    Search Records
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>

                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Select Month/Year/Branch and click Search.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      {columns.map((col) => (
                        <th key={col} className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors duration-200`}
                      >
                        {columns.map((col) => (
                          <td
                            key={col}
                            className={`px-4 py-3 whitespace-nowrap text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {r?.[col] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalRows > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalRows)} of {totalRows} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1 || loadingList}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      disabled={loadingList}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages || loadingList}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default DayWiseLubeOutwardReport;